import {
  deleteV1CompaniesCompanyIdTaxRateSchedulesId,
  getV1CompaniesCompanyIdTaxRateSchedules,
  patchV1CompaniesCompanyIdTaxRateSchedulesId,
  postV1CompaniesCompanyIdTaxRateSchedules,
  TaxRateSchedule_Create,
  TaxRateSchedule_Update,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import { TAX_RATE_SCHEDULE_QUERY_KEY } from '~queries/constants';
import { queryClient } from '~queries/queryClient';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import rethrowIfAborted from '~utils/rethrowIfAborted';

interface GetTaxRateSchedulesParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

export default function useTaxRateSchedule() {
  const getTaxRateSchedules = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      searchQuery,
      signal,
    }: GetTaxRateSchedulesParams) => {
      try {
        const response = await getV1CompaniesCompanyIdTaxRateSchedules({
          path: { 'company-id': companyId },
          query: {
            ...(limit ? { 'page[limit]': limit } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
            ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
          },
          signal,
        });
        const data = response.data.data.map(TaxRateSchedule.parse);
        const pagination = extractPagination(response);

        return { data, pagination };
      } catch (error) {
        rethrowIfAborted(error, signal);

        connection.handleRequestError(
          error,
          t('error_messages.tax_rate_schedules.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const createTaxRateSchedule = useCallback(
    async (companyId: string, body: TaxRateSchedule_Create) => {
      try {
        const response = await postV1CompaniesCompanyIdTaxRateSchedules({
          path: { 'company-id': companyId },
          body,
        });

        return TaxRateSchedule.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.tax_rate_schedules.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const updateTaxRateSchedule = useCallback(
    async (
      companyId: string,
      taxRateScheduleId: string,
      body: TaxRateSchedule_Update,
    ) => {
      try {
        const response = await patchV1CompaniesCompanyIdTaxRateSchedulesId({
          path: { 'company-id': companyId, id: taxRateScheduleId },
          body,
        });

        await queryClient.invalidateQueries({ queryKey: [TAX_RATE_SCHEDULE_QUERY_KEY] });

        return TaxRateSchedule.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.tax_rate_schedules.failed_to_update'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteTaxRateSchedule = useCallback(
    async (companyId: string, taxRateScheduleId: string) => {
      try {
        await deleteV1CompaniesCompanyIdTaxRateSchedulesId({
          path: { 'company-id': companyId, id: taxRateScheduleId },
        });

        await queryClient.invalidateQueries({ queryKey: [TAX_RATE_SCHEDULE_QUERY_KEY] });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.tax_rate_schedules.failed_to_delete'),
        );
        throw error;
      }
    },
    [],
  );

  return {
    createTaxRateSchedule,
    deleteTaxRateSchedule,
    getTaxRateSchedules,
    updateTaxRateSchedule,
  };
}
